import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";

export default function AddStore({ modalSetting, handlePageUpdate, authContext }) {
  const [form, setForm] = useState({
    userId: "",
    name: "",
    category: "",
    address: "",
    city: "",
  });

  const [open, setOpen] = useState(true);
  const cancelButtonRef = useRef(null);

  // Set user ID from authContext on load
  useEffect(() => {
    if (authContext?.user) {
      setForm(prev => ({ ...prev, userId: authContext.user }));
    }
  }, [authContext]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const addStore = () => {
    const payload = {
      user_id: form.userId,
      name: form.name,
      category: form.category,
      address: form.address,
      city: form.city,
    };
  
    fetch("https://stock.krishtec.co.in/stock/add_store.php", {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "Store added successfully") {
          alert("STORE ADDED");
  
          // ✅ Reset form
          setForm({
            userId: authContext.user,
            name: "",
            category: "",
            address: "",
            city: "",
          });
  
          // ✅ Close modal first
          setOpen(false);
          modalSetting();
  
          // ✅ Then refresh parent list
          setTimeout(() => {
            handlePageUpdate();
          }, 200); // short delay to ensure modal closes before fetch
        } else {
          alert("Error: " + data.message);
        }
      })
      .catch((err) => {
        console.log("Fetch error:", err);
      });
  };
  

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={() => {
          setOpen(false);
          modalSetting();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <PlusIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        Store Information
                      </Dialog.Title>

                      <form className="mt-4">
                        <div className="grid gap-4 mb-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">
                              Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              id="name"
                              value={form.name}
                              onChange={handleInputChange}
                              className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                              placeholder="Enter Store Name"
                            />
                          </div>

                          <div>
                            <label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-900">
                              City
                            </label>
                            <input
                              type="text"
                              name="city"
                              id="city"
                              value={form.city}
                              onChange={handleInputChange}
                              className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                              placeholder="Enter City Name"
                            />
                          </div>

                          <div>
                            <label htmlFor="category" className="block mb-2 text-sm font-medium text-gray-900">
                              Category
                            </label>
                            <select
                              id="category"
                              name="category"
                              value={form.category}
                              onChange={handleInputChange}
                              className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                            >
                              <option value="">Select Category</option>
                              <option value="Electronics">Electronics</option>
                              <option value="Groceries">Groceries</option>
                              <option value="Wholesale">Wholesale</option>
                              <option value="SuperMart">SuperMart</option>
                              <option value="Phones">Phones</option>
                            </select>
                          </div>

                          <div className="sm:col-span-2">
                            <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900">
                              Address
                            </label>
                            <textarea
                              id="address"
                              name="address"
                              rows="4"
                              value={form.address}
                              onChange={handleInputChange}
                              className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                              placeholder="Write an address..."
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    onClick={addStore}
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                  >
                    Add Store
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      modalSetting();
                    }}
                    ref={cancelButtonRef}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
